import React, { useEffect, useState } from 'react';
import { Alert, Button, Typography, Space, theme, message } from 'antd';
import { WarningOutlined } from '@ant-design/icons';

import { useAuth } from '../../../context/authProvider';
import OrganizationService from '../services';

const { Text } = Typography;

const formatScheduledDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Prominent org-wide warning shown whenever the organization has a scheduled
 * deletion pending (organization.pending_deletion). Every member sees the
 * warning; only the owner (user.root_admin) gets the Restore button.
 *
 * Refreshes the organization status on mount so the banner reflects the
 * latest state even if the cached auth/context data is stale.
 */
const PendingDeletionBanner = () => {
  const { token } = theme.useToken();
  const { auth, updateAuth } = useAuth();
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    OrganizationService.getOrganization()
      .then((res) => {
        if (res?.data?.success && res.data.data) {
          updateAuth({ organization: res.data.data });
        }
      })
      .catch(() => {
        /* non-critical background refresh */
      });
    // Only refresh once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const organization = auth?.organization;
  const isOwner = !!auth?.user?.root_admin;

  if (!organization?.pending_deletion) {
    return null;
  }

  const handleRestore = async () => {
    setRestoring(true);
    try {
      const res = await OrganizationService.restoreOrganization();
      if (res?.data?.success) {
        message.success(res.data.message || 'Deletion cancelled. Your organization has been restored.');
        const orgRes = await OrganizationService.getOrganization();
        if (orgRes?.data?.success && orgRes.data.data) {
          updateAuth({ organization: orgRes.data.data });
        }
      } else {
        message.error(res?.data?.message || 'Could not restore organization.');
      }
    } catch (err) {
      message.error(err?.response?.data?.message || 'Could not restore organization.');
    } finally {
      setRestoring(false);
    }
  };

  const scheduledDate = formatScheduledDate(organization.scheduled_deletion_at);

  return (
    <Alert
      type="error"
      showIcon
      banner
      icon={<WarningOutlined />}
      message="This organization is scheduled for deletion"
      description={
        <Space orientation="vertical" size="small" style={{ width: '100%' }}>
          <Text>
            All data will be permanently erased on <strong>{scheduledDate || 'the scheduled date'}</strong>.
            {isOwner
              ? ' You can restore the organization and cancel the deletion any time before then.'
              : ' Only the organization owner can sign in and restore it before then.'}
          </Text>
          {isOwner && (
            <Button size="small" type="primary" danger loading={restoring} onClick={handleRestore}>
              Restore organization
            </Button>
          )}
        </Space>
      }
      style={{ borderRadius: token.borderRadiusLG, marginBottom: token.marginMD }}
    />
  );
};

export default PendingDeletionBanner;
